'use client';

import { useCallback } from 'react';
import { useServices } from '../../application/providers/ServiceProvider';
import type { IProductRepository } from '../../domain/repositories/IProductRepository';
import type { IOrderRepository } from '../../domain/repositories/IOrderRepository';
import type { ICartRepository } from '../../domain/repositories/ICartRepository';
import type { CachedFirebaseProductRepository } from '../repositories/CachedFirebaseProductRepository';
import type { CachedFirebaseOrderRepository } from '../repositories/CachedFirebaseOrderRepository';
import type { ProductDocument } from '@/types/manage.types';
import type { CreateOrderDto } from '../../application/dtos/OrderDto';

/**
 * Hook for accessing repositories through the DI container
 * Provides type-safe access to cached repository implementations
 */
export function useRepositories() {
  const services = useServices();

  return {
    productRepository: services.productRepository as unknown as CachedFirebaseProductRepository,
    orderRepository: services.orderRepository as unknown as CachedFirebaseOrderRepository,
    cartRepository: services.cartRepository as unknown as ICartRepository,
  };
}

/**
 * Hook for accessing product repository specifically
 */
export function useProductRepository(): IProductRepository {
  const services = useServices();
  return services.productRepository as unknown as IProductRepository;
}

/**
 * Hook for accessing order repository specifically
 */
export function useOrderRepository(): IOrderRepository {
  const services = useServices();
  return services.orderRepository as unknown as IOrderRepository;
}

/**
 * Hook for accessing cart repository specifically
 */
export function useCartRepository(): ICartRepository {
  const services = useServices();
  return services.cartRepository as unknown as ICartRepository;
}

/**
 * Hook for repository cache management
 */
export function useRepositoryCache() {
  const { productRepository, orderRepository } = useRepositories();

  const clearAllCaches = useCallback(() => {
    (productRepository as CachedFirebaseProductRepository).clearAllCaches();
    (orderRepository as CachedFirebaseOrderRepository).clearAllCaches();
  }, [productRepository, orderRepository]);

  const getCacheStats = useCallback(() => {
    return {
      products: (productRepository as CachedFirebaseProductRepository).getCacheStats(),
      orders: (orderRepository as CachedFirebaseOrderRepository).getCacheStats(),
    };
  }, [productRepository, orderRepository]);

  const warmUpCaches = useCallback(async () => {
    await Promise.all([
      (productRepository as CachedFirebaseProductRepository).warmUpCache(),
      (orderRepository as CachedFirebaseOrderRepository).warmUpCache(),
    ]);
  }, [productRepository, orderRepository]);

  return {
    clearAllCaches,
    getCacheStats,
    warmUpCaches,
  };
}

/**
 * Hook for repository operations with error handling
 */
export function useRepositoryOperations() {
  const productRepository = useProductRepository();
  const orderRepository = useOrderRepository();

  // Product operations
  const findProductById = useCallback(
    async (id: string) => {
      try {
        return await productRepository.findProductById(id);
      } catch (error) {
        console.error('Error finding product:', error);
        throw error;
      }
    },
    [productRepository],
  );

  const findAllProducts = useCallback(
    async (limit?: number) => {
      try {
        return await productRepository.findAllProducts(limit);
      } catch (error) {
        console.error('Error finding products:', error);
        throw error;
      }
    },
    [productRepository],
  );

  const saveProduct = useCallback(
    async (product: ProductDocument) => {
      try {
        return await productRepository.saveProduct(product);
      } catch (error) {
        console.error('Error saving product:', error);
        throw error;
      }
    },
    [productRepository],
  );

  // Order operations
  const findOrderById = useCallback(
    async (id: string) => {
      try {
        return await orderRepository.findOrderById(id);
      } catch (error) {
        console.error('Error finding order:', error);
        throw error;
      }
    },
    [orderRepository],
  );

  const findAllOrders = useCallback(
    async (limit?: number) => {
      try {
        return await orderRepository.findAllOrders(limit);
      } catch (error) {
        console.error('Error finding orders:', error);
        throw error;
      }
    },
    [orderRepository],
  );

  const submitOrder = useCallback(
    async (orderData: CreateOrderDto) => {
      try {
        return await orderRepository.submitOrder(orderData);
      } catch (error) {
        console.error('Error submitting order:', error);
        throw error;
      }
    },
    [orderRepository],
  );

  return {
    // Product operations
    findProductById,
    findAllProducts,
    saveProduct,

    // Order operations
    findOrderById,
    findAllOrders,
    submitOrder,
  };
}
